// Round-trip URL search params <-> typed filter object for /custom-regions.
// Used by the route's `validateSearch` and consumed by the custom-regions
// header + table widgets via `useSearch`.
//
// Mirrors the itineraries convention at
// `apps/itineraries/web/src/features/itineraries-filters/model/search-params.ts`:
// a pure URL coercer, no Zod parse, no exceptions. TanStack Router's
// `validateSearch` rebuilds this object on every URL parse so the function
// returns plain optional fields — fields the URL doesn't carry come back
// as `undefined`, which the tRPC transformer strips at the wire.

export type CustomRegionsSearch = {
  title?: string;
  countries?: string[];
  cities?: string[];
  labels?: string[];
  page?: number;
  pageSize?: number;
};

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v : undefined;

const asStringArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === 'string');
  return out.length > 0 ? out : undefined;
};

const asNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }
  return undefined;
};

export const validateCustomRegionsSearch = (
  input: Record<string, unknown>
): CustomRegionsSearch => ({
  title: asString(input.title),
  countries: asStringArray(input.countries),
  cities: asStringArray(input.cities),
  labels: asStringArray(input.labels),
  page: asNumber(input.page),
  pageSize: asNumber(input.pageSize),
});
